<template>
  <div class="analytics-panel">

    <div class="analytics-section">
      <div class="analytics-section-title">Page Activity Summary</div>
      <div v-if="summaryRows.length === 0" class="analytics-empty">No page activity recorded yet.</div>
      <table v-else class="analytics-table">
        <thead>
          <tr>
            <th>page</th>
            <th>event</th>
            <th>count</th>
            <th>avg load time</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in summaryRows" :key="i">
            <td>{{ row.entity_name || '–' }}</td>
            <td>
              <span class="event-badge" :class="'event-' + eventColor(row.event_type)">{{ formatEvent(row.event_type) }}</span>
            </td>
            <td>{{ formatNumber(row.count) }}</td>
            <td>{{ row.avg_duration_ms != null ? Math.round(row.avg_duration_ms) + ' ms' : '–' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="analytics-section">
      <div class="analytics-section-title">Logins</div>
      <div class="stat-cards">
        <div class="stat-card">
          <div class="stat-card-value">{{ formatNumber(loginCount) }}</div>
          <div class="stat-card-label">total logins</div>
        </div>
      </div>
    </div>

    <div v-for="panel in chartPanels" :key="panel.id" class="analytics-section">
      <div class="analytics-section-title">{{ panel.title }}</div>
      <div v-if="activityDays.length === 0" class="analytics-empty">No activity recorded yet.</div>
      <div v-else class="line-chart-wrap" @mouseleave="clearHover">
        <svg
          class="line-chart"
          :viewBox="`0 0 ${panel.chart.width} ${panel.chart.height}`"
          role="img"
          :aria-label="panel.title"
        >
          <line
            v-for="tick in panel.chart.ticks"
            :key="panel.id + '-grid-' + tick.value"
            :x1="panel.chart.pad.l"
            :x2="panel.chart.width - panel.chart.pad.r"
            :y1="tick.y"
            :y2="tick.y"
            class="line-grid"
          />
          <text
            v-for="tick in panel.chart.ticks"
            :key="panel.id + '-y-' + tick.value"
            :x="panel.chart.pad.l - 8"
            :y="tick.y + 3"
            class="line-axis-label"
            text-anchor="end"
          >{{ tick.label }}</text>
          <g v-for="series in panel.chart.series" :key="panel.id + '-' + series.key">
            <polyline
              v-for="(segment, segmentIndex) in series.segments"
              :key="series.key + '-' + segmentIndex"
              :points="segment"
              fill="none"
              :stroke="series.color"
              stroke-width="2"
              stroke-linejoin="round"
              stroke-linecap="round"
            />
          </g>
          <line
            v-if="hoverChart === panel.id && hoverColumn"
            :x1="hoverColumn.x"
            :x2="hoverColumn.x"
            :y1="panel.chart.pad.t"
            :y2="panel.chart.height - panel.chart.pad.b"
            class="line-hover-guide"
          />
          <g v-for="series in panel.chart.series" :key="panel.id + '-pts-' + series.key">
            <circle
              v-for="point in series.dots"
              :key="series.key + '-' + point.index"
              :cx="point.x"
              :cy="point.y"
              :r="hoverChart === panel.id && hoverIndex === point.index ? panel.chart.dotR + 1.4 : panel.chart.dotR"
              :fill="series.color"
              class="line-dot"
            />
          </g>
          <text
            v-for="column in panel.chart.columns"
            v-show="column.showLabel"
            :key="panel.id + '-x-' + column.index"
            :x="column.x"
            :y="panel.chart.height - 10"
            class="line-axis-label"
            text-anchor="middle"
          >{{ column.label }}</text>
          <rect
            v-for="column in panel.chart.columns"
            :key="panel.id + '-hit-' + column.index"
            :x="column.hitX"
            :y="column.hitY"
            :width="column.hitW"
            :height="column.hitH"
            class="line-hit"
            @mouseenter="setHover(panel.id, column.index)"
          />
        </svg>
        <div v-if="hoverChart === panel.id && hoverColumn" class="line-tooltip" :style="hoverTipStyle">
          <div class="line-tooltip-date">{{ hoverColumn.label }}</div>
          <div v-for="series in panel.chart.series" :key="panel.id + '-tip-' + series.key" class="line-tooltip-row">
            <span class="legend-swatch" :style="{ background: series.color }"></span>
            <span>{{ series.label }}</span>
            <strong>{{ formatNumber(hoverColumn[series.key]) }}</strong>
          </div>
        </div>
        <div class="chart-legend">
          <span v-for="series in panel.chart.series" :key="panel.id + '-legend-' + series.key" class="legend-item">
            <span class="legend-swatch" :style="{ background: series.color }"></span>
            {{ series.label }}
          </span>
        </div>
      </div>
    </div>

  </div>
</template>

<script>

import axios from 'axios'
import { buildActivityLineChart, pageActivityDays } from '../../lib/activity-line-chart.js'

export default {
  name: 'AnalyticsPages',
  props: {
    application: String,
    from: String,
    to: String,
    refreshKey: Number
  },
  data () {
    return {
      summaryRows: [],
      activityDays: [],
      hoverChart: null,
      hoverIndex: null
    }
  },
  computed: {
    loginCount () {
      return this.summaryRows
        .filter(r => r.event_type === 'login')
        .reduce((sum, r) => sum + parseInt(r.count), 0)
    },
    chartPanels () {
      const format = (date) => this.formatDate(date)
      return [
        {
          id: 'activity',
          title: 'Activity Over Time',
          chart: buildActivityLineChart(this.activityDays, format, 'activity')
        },
        {
          id: 'people',
          title: 'Logins and Users',
          chart: buildActivityLineChart(this.activityDays, format, 'people')
        }
      ]
    },
    hoveredChart () {
      const panel = this.chartPanels.find(item => item.id === this.hoverChart)
      return panel ? panel.chart : null
    },
    hoverColumn () {
      if (!this.hoveredChart || this.hoverIndex == null) return null
      return this.hoveredChart.columns[this.hoverIndex] || null
    },
    hoverTipStyle () {
      const column = this.hoverColumn
      const chart = this.hoveredChart
      if (!column || !chart) return {}
      const peakValues = chart.series
        .map(series => column[series.key])
        .filter(value => value != null && Number.isFinite(Number(value)))
      const peak = peakValues.length ? Math.max(...peakValues) : 0
      const y = chart.pad.t + chart.plotH * (1 - peak / chart.max)
      const left = Math.min(84, Math.max(16, (column.x / chart.width) * 100))
      const top = Math.max(6, (y / chart.height) * 100)
      return {
        left: left + '%',
        top: top + '%',
        transform: 'translate(-50%, calc(-100% - 8px))'
      }
    }
  },
  watch: {
    refreshKey: 'loadData'
  },
  mounted () {
    this.loadData()
  },
  methods: {
    async loadData () {

      try {

        const params = {}
        if (this.from) params.from = this.from
        if (this.to) params.to = this.to

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/analytics/pages`, {
          headers: { 'application-unique-title': this.application },
          params
        })

        this.summaryRows = res.data.pageCounts || []
        this.clearHover()
        this.activityDays = pageActivityDays(
          res.data.activityOverTime,
          res.data.uniqueUsersOverTime,
          res.data.usersTrackedFrom
        )

      } catch (err) {
        console.log('failed to load page analytics', err)
      }
    },
    setHover (chartId, index) {
      this.hoverChart = chartId
      this.hoverIndex = index
    },
    clearHover () {
      this.hoverChart = null
      this.hoverIndex = null
    },
    formatNumber (n) {
      if (n == null || n === '') return '–'
      return Number(n).toLocaleString()
    },
    formatEvent (eventType) {

      const map = {
        page_load: 'page load',
        page_component_load: 'component load',
        page_component_submit: 'form submit',
        login: 'login'
      }

      return map[eventType] || eventType
    },
    eventColor (eventType) {
      if (eventType === 'login') return 'purple'
      if (eventType.includes('submit')) return 'green'
      if (eventType.includes('load')) return 'blue'
      return 'gray'
    },
    formatDate (dateStr) {
      const d = new Date(dateStr + 'T00:00:00')
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
  }
}
</script>

<style scoped>

.analytics-panel {
  max-width: 960px;
}

.analytics-section {
  margin-bottom: 28px;
}

.analytics-section-title {
  font-size: 0.78rem;
  font-weight: 600;
  color: #a0a09e;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
}

.analytics-empty {
  color: #a8a8a4;
  font-size: 0.8rem;
  padding: 12px 0;
}

/* ── Stat cards ── */
.stat-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.stat-card {
  background: #1a1a17;
  border: 1px solid #2a2a27;
  border-radius: 6px;
  padding: 12px 16px;
  min-width: 120px;
}

.stat-card-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #e8e8e6;
  line-height: 1.2;
}

.stat-card-label {
  font-size: 0.7rem;
  color: #707070;
  margin-top: 2px;
}

/* ── Table ── */
.analytics-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.analytics-table th {
  text-align: left;
  color: #707070;
  font-weight: 500;
  padding: 6px 10px;
  border-bottom: 1px solid #2a2a27;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.analytics-table td {
  padding: 7px 10px;
  color: #a0a09e;
  border-bottom: 1px solid #1e1e1b;
}

.analytics-table tr:hover td {
  background: #1a1a17;
}

.event-badge {
  font-size: 0.68rem;
  padding: 2px 7px;
  border-radius: 3px;
  font-weight: 500;
}

.event-blue { color: #4a9eff; background: rgba(74, 158, 255, 0.1); }
.event-green { color: #6cc644; background: rgba(108, 198, 68, 0.1); }
.event-purple { color: #b48eff; background: rgba(180, 142, 255, 0.1); }
.event-gray { color: #707070; background: rgba(112, 112, 112, 0.1); }

/* ── Line chart ── */
.line-chart-wrap {
  position: relative;
}

.line-chart {
  width: 100%;
  height: auto;
  display: block;
}

.line-grid {
  stroke: #2a2a27;
  stroke-width: 1;
}

.line-hover-guide {
  stroke: #3a3a36;
  stroke-width: 1;
  stroke-dasharray: 3 3;
}

.line-axis-label {
  fill: #a8a8a4;
  font-size: 11px;
}

.line-dot {
  stroke: #141412;
  stroke-width: 1;
}

.line-hit {
  fill: transparent;
  cursor: crosshair;
}

.line-tooltip {
  position: absolute;
  z-index: 2;
  pointer-events: none;
  background: #1a1a17;
  border: 1px solid #2a2a27;
  border-radius: 6px;
  padding: 8px 10px;
  min-width: 140px;
}

.line-tooltip-date {
  color: #e8e8e6;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 6px;
}

.line-tooltip-row {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #a0a09e;
  font-size: 0.72rem;
  margin-top: 3px;
}

.line-tooltip-row strong {
  margin-left: auto;
  color: #e8e8e6;
  font-weight: 600;
}

.chart-legend {
  display: flex;
  gap: 14px;
  margin-top: 8px;
  padding-left: 52px;
}

.legend-item {
  font-size: 0.68rem;
  color: #707070;
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-swatch {
  width: 14px;
  height: 3px;
  border-radius: 2px;
}
</style>
